import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank, Close, Public } from "@mui/icons-material";
import { Autocomplete, Box, Button, Checkbox, Divider, FormControl, IconButton, Link, MenuItem, Select, TextField, Typography } from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { DateFilterType, calculateDateFilter } from '@frontend/features/cases/utils/dateFilters';

import { CaseStatus } from "@bandido/shared/constants";
import { OriginatedByChannel } from "@bandido/shared/types";
import { useGetAllClients } from "@frontend/features/clients/queries";
import { useGetAllUsers } from "@frontend/features/users/queries";
import { useGetAllTeams } from "@frontend/features/teams/queries";
import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from "@frontend/features/workflows/queries";
import { GetFilterByIdDto, StepOptionsResponseDto, TeamResponseDto, WorkflowResponseDto } from "@bandido/shared/dtos";
import { FilterItem } from "@frontend/features/common/components/FilterItem";
import { useGetMyAgencies } from "@frontend/features/agencies/queries";
import { useGetTokenOrganizations } from "@frontend/features/organizations/queries";
import { useGetFieldBlockByWorkflowIds } from '@frontend/features/custom-fields/queries';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import React from "react";

interface Props {
  title: string;
  filter: GetFilterByIdDto;
  onSave: (filterId: string, values: { name: string; isPublic: boolean, params: Record<string, unknown> }) => void;
  onClose?: () => void;
}

export const EditCaseListFilers = ({ title, onClose, filter, onSave }: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState(filter.name);
  const [params, setParams] = useState<Record<string, unknown>>(filter.params)
  const [isPublic, setIsPublic] = useState(filter.isPublic);
  const [isSaving, setIsSaving] = useState(false);

  const toggleIsPublic = () => setIsPublic(s => !s);

  const [teamSearch, setTeamSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  // Nuevos estados para filtros de fecha avanzados
  const [createdDateType, setCreatedDateType] = useState<string>('');
  const [updatedDateType, setUpdatedDateType] = useState<string>('');
  const [customDaysCreated, setCustomDaysCreated] = useState('');
  const [customDaysUpdated, setCustomDaysUpdated] = useState('');
  const [specificDateCreated, setSpecificDateCreated] = useState('');
  const [specificDateUpdated, setSpecificDateUpdated] = useState('');
  const [dateRangeCreated, setDateRangeCreated] = useState({ from: '', to: '' });
  const [dateRangeUpdated, setDateRangeUpdated] = useState({ from: '', to: '' });

  // Estados debounced para rangos de fechas
  const [debouncedDateRangeCreated, setDebouncedDateRangeCreated] = useDebouncedState(
    { from: '', to: '' }, 
    800
  );
  const [debouncedDateRangeUpdated, setDebouncedDateRangeUpdated] = useDebouncedState(
    { from: '', to: '' }, 
    800
  );

  // Estados para custom date fields
  const [customDateFieldStates, setCustomDateFieldStates] = useState<Record<string, {
    type: string;
    customDays: string;
    nextCustomDays: string;
    specificDate: string;
    dateRange: { from: string; to: string };
  }>>({});

  // Estados debounced para custom date fields
  const [debouncedCustomDateFieldStates, setDebouncedCustomDateFieldStates] = useDebouncedState<
    Record<string, { from: string; to: string }>
  >({}, 800);

  // Estados debounced para fechas específicas
  const [debouncedSpecificDateCreated, setDebouncedSpecificDateCreated] = useDebouncedState('', 800);
  const [debouncedSpecificDateUpdated, setDebouncedSpecificDateUpdated] = useDebouncedState('', 800);

  // Estados debounced para custom date fields - fecha específica
  const [debouncedCustomDateFieldSpecificDates, setDebouncedCustomDateFieldSpecificDates] = useDebouncedState<
    Record<string, string>
  >({}, 800);

  // Estado debounced para nextCustomDays en custom date fields
  const [debouncedCustomDateFieldNextCustomDays, setDebouncedCustomDateFieldNextCustomDays] = useDebouncedState<
    Record<string, string>
  >({}, 800);

  // Ref para controlar si ya se inicializó desde params
  const isInitializedRef = useRef(false);
  
  // Ref para trackear el valor anterior de workflowIds
  const previousWorkflowIdsRef = useRef<string | null>(null);

  // Inicializar estados desde filter.params
  useEffect(() => {
    if (!isInitializedRef.current) {
      // Convertir los valores pipe-separated a arrays para los autocompletes
      const convertedParams = { ...filter.params };
      
      // Convertir campos que deben ser arrays para los autocompletes
      const arrayFields = ['workflowIds', 'teamIds', 'stepIds', 'clientIds', 'agentIds', 'status', 'originatedByChannel', 'organizations', 'agencies'];
      
      arrayFields.forEach(field => {
        if (convertedParams[field] && typeof convertedParams[field] === 'string') {
          const stringValue = convertedParams[field] as string;
          if (stringValue.includes('|')) {
            // Convertir de "val1|val2" a ["val1", "val2"]
            convertedParams[field] = stringValue.split('|').filter(Boolean);
          } else if (stringValue.trim() !== '') {
            // Convertir single value a array
            convertedParams[field] = [stringValue];
          }
        }
      });

      setParams(convertedParams);
      setName(filter.name)
      setIsPublic(filter.isPublic);

      // Inicializar estados de filtros de fecha desde params
      if (filter.params.createdDateFilter) {
        setCreatedDateType(filter.params.createdDateFilter as string);
        if (filter.params.createdCustomDays) {
          setCustomDaysCreated(filter.params.createdCustomDays as string);
        }
        if (filter.params.createdSpecificDate) {
          setSpecificDateCreated(filter.params.createdSpecificDate as string);
          setDebouncedSpecificDateCreated(filter.params.createdSpecificDate as string);
        }
        if (filter.params.createdDateFrom && filter.params.createdDateTo) {
          const range = { 
            from: filter.params.createdDateFrom as string, 
            to: filter.params.createdDateTo as string 
          };
          setDateRangeCreated(range);
          setDebouncedDateRangeCreated(range);
        }
      }

      if (filter.params.updatedDateFilter) {
        setUpdatedDateType(filter.params.updatedDateFilter as string);
        if (filter.params.updatedCustomDays) {
          setCustomDaysUpdated(filter.params.updatedCustomDays as string);
        }
        if (filter.params.updatedSpecificDate) {
          setSpecificDateUpdated(filter.params.updatedSpecificDate as string);
          setDebouncedSpecificDateUpdated(filter.params.updatedSpecificDate as string);
        }
        if (filter.params.updatedDateFrom && filter.params.updatedDateTo) {
          const range = { 
            from: filter.params.updatedDateFrom as string, 
            to: filter.params.updatedDateTo as string 
          };
          setDateRangeUpdated(range);
          setDebouncedDateRangeUpdated(range);
        }
      }

      // Inicializar custom date field states desde params
      const customDateStates: Record<string, any> = {};
      const debouncedRanges: Record<string, { from: string; to: string }> = {};
      const debouncedSpecificDates: Record<string, string> = {};
      const debouncedNextCustomDays: Record<string, string> = {};
      
      for (const [key, value] of Object.entries(filter.params)) {
        // Buscar parámetros que sigan el patrón fieldBlockId_fieldName_filter
        const filterMatch = key.match(/^(.+)_filter$/);
        if (filterMatch) {
          const baseKey = filterMatch[1];
          const filterType = value as string;
          
          if (filterType) {
            const specificDate = (filter.params[`${baseKey}_specificDate`] as string) || '';
            const nextCustomDays = (filter.params[`${baseKey}_nextCustomDays`] as string) || '';
            
            // Para dateRange, derivar desde after y before
            const after = (filter.params[`${baseKey}_after`] as string) || '';
            const before = (filter.params[`${baseKey}_before`] as string) || '';
            const dateRange = {
              from: after ? after.split('T')[0] : '', // Extraer fecha de "2025-07-01T00:00:00"
              to: before ? before.split('T')[0] : ''   // Extraer fecha de "2025-07-31T23:59:59"
            };
            
            customDateStates[baseKey] = {
              type: filterType,
              customDays: (filter.params[`${baseKey}_customDays`] as string) || '',
              nextCustomDays,
              specificDate,
              dateRange
            };
            
            // Inicializar estados debounced
            if (filterType === 'dateRange' && (dateRange.from || dateRange.to)) {
              debouncedRanges[baseKey] = dateRange;
            }
            if (filterType === 'specificDate' && specificDate) {
              debouncedSpecificDates[baseKey] = specificDate;
            }
            if (filterType === 'nextCustomDays' && nextCustomDays) {
              debouncedNextCustomDays[baseKey] = nextCustomDays;
            }
          }
        }
      }
      
      if (Object.keys(customDateStates).length > 0) {
        setCustomDateFieldStates(customDateStates);
      }
      
      // Inicializar estados debounced para custom date fields
      if (Object.keys(debouncedRanges).length > 0) {
        setDebouncedCustomDateFieldStates(debouncedRanges);
      }
      if (Object.keys(debouncedSpecificDates).length > 0) {
        setDebouncedCustomDateFieldSpecificDates(debouncedSpecificDates);
      }
      if (Object.keys(debouncedNextCustomDays).length > 0) {
        setDebouncedCustomDateFieldNextCustomDays(debouncedNextCustomDays);
      }

      // Marcar como inicializado después de procesar params
      isInitializedRef.current = true;
    }
  }, [filter]);

  // Efecto para limpiar custom field filters cuando cambie workflowIds
  useEffect(() => {
    const currentWorkflowIds = Array.isArray(params.workflowIds) 
      ? (params.workflowIds as string[]).join('|') 
      : (typeof params.workflowIds === 'string' ? params.workflowIds : '');
    
    // Si ya se inicializó y workflowIds cambió, limpiar custom field filters
    if (isInitializedRef.current && previousWorkflowIdsRef.current !== null && 
        previousWorkflowIdsRef.current !== currentWorkflowIds) {
      
      // Encontrar todos los parámetros que son custom fields
      const newParams = { ...params };
      let hasCustomFieldParams = false;
      
      for (const key of Object.keys(params)) {
        // Buscar parámetros que contengan UUID (fieldBlockId) seguido de _
        if (key.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/)) {
          delete newParams[key];
          hasCustomFieldParams = true;
        }
      }
      
      // Si hay custom field params, limpiarlos
      if (hasCustomFieldParams) {
        setParams(newParams);
        
        // También limpiar los estados locales
        setCustomDateFieldStates({});
        setDebouncedCustomDateFieldStates({});
        setDebouncedCustomDateFieldSpecificDates({});
        setDebouncedCustomDateFieldNextCustomDays({});
      }
    }
    
    // Actualizar el ref con el valor actual
    previousWorkflowIdsRef.current = currentWorkflowIds;
  }, [params.workflowIds]);

  // Efectos debounced para custom date fields
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldStates).forEach(fieldKey => {
        const debouncedRange = debouncedCustomDateFieldStates[fieldKey];
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'dateRange' && 
            (debouncedRange.from || debouncedRange.to)) {
          
          // Verificar si los valores actuales en params son diferentes antes de actualizar
          const currentAfter = (params[`${fieldKey}_after`] as string) || '';
          const currentBefore = (params[`${fieldKey}_before`] as string) || '';
          
          const expectedAfter = debouncedRange.from ? `${debouncedRange.from}T00:00:00` : '';
          const expectedBefore = debouncedRange.to ? `${debouncedRange.to}T23:59:59` : '';
          
          if (currentAfter !== expectedAfter || currentBefore !== expectedBefore) {
            handleCustomDateFieldChange(fieldKey, 'dateRange', debouncedRange);
          }
        }
      });
    }
  }, [debouncedCustomDateFieldStates, customDateFieldStates]);

  // Efecto para manejar cambios en fecha específica creada debounced
  useEffect(() => {
    if (isInitializedRef.current && createdDateType === 'specificDate' && debouncedSpecificDateCreated) {
      handleDateFilterChange('created', 'specificDate', debouncedSpecificDateCreated);
    }
  }, [debouncedSpecificDateCreated, createdDateType]);

  // Efecto para manejar cambios en fecha específica actualizada debounced
  useEffect(() => {
    if (isInitializedRef.current && updatedDateType === 'specificDate' && debouncedSpecificDateUpdated) {
      handleDateFilterChange('updated', 'specificDate', debouncedSpecificDateUpdated);
    }
  }, [debouncedSpecificDateUpdated, updatedDateType]);

  // Efecto para manejar cambios en custom date fields - fecha específica debounced
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldSpecificDates).forEach(fieldKey => {
        const debouncedDate = debouncedCustomDateFieldSpecificDates[fieldKey];
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'specificDate' && debouncedDate) {
          handleCustomDateFieldChange(fieldKey, 'specificDate', debouncedDate);
        }
      });
    }
  }, [debouncedCustomDateFieldSpecificDates, customDateFieldStates]);

  // Efecto para manejar cambios en custom date fields - nextCustomDays debounced
  useEffect(() => {
    if (isInitializedRef.current) {
      Object.keys(debouncedCustomDateFieldNextCustomDays).forEach(fieldKey => {
        const debouncedDays = debouncedCustomDateFieldNextCustomDays[fieldKey];
        const currentState = customDateFieldStates[fieldKey];
        
        if (currentState && currentState.type === 'nextCustomDays' && debouncedDays) {
          handleCustomDateFieldChange(fieldKey, 'nextCustomDays', debouncedDays);
        }
      });
    }
  }, [debouncedCustomDateFieldNextCustomDays, customDateFieldStates]);

  const handleClearFilters = () => {
    setParams({
      status: '',
      teamIds: '',
      workflowIds: '',
      agencies: '',
      organizations: '',
      subject: '',
      originatedByChannel: '',
      agentIds: '',
      clientIds: '',
      stepIds: '',
      createdBeforeDate: '',
      createdAfterDate: '',
      sort: 'createdAt',
      order: 'DESC'
    });

    // Reset estados de filtros de fecha
    setCreatedDateType('');
    setUpdatedDateType('');
    setCustomDaysCreated('');
    setCustomDaysUpdated('');
    setSpecificDateCreated('');
    setSpecificDateUpdated('');
    setDateRangeCreated({ from: '', to: '' });
    setDateRangeUpdated({ from: '', to: '' });
    
    // Reset custom date fields
    setCustomDateFieldStates({});
    
    // Reset filtros de búsqueda
    setClientSearch('');
    setTeamSearch('');
    setAgentSearch('');
    setWorkflowSearch('');
    setStepSearch('');
    
    // Reset estados debounced
    setDebouncedDateRangeCreated({ from: '', to: '' });
    setDebouncedDateRangeUpdated({ from: '', to: '' });
    setDebouncedSpecificDateCreated('');
    setDebouncedSpecificDateUpdated('');
    setDebouncedCustomDateFieldStates({});
    setDebouncedCustomDateFieldSpecificDates({});
    setDebouncedCustomDateFieldNextCustomDays({});
    
    // Reset flag de inicialización
    isInitializedRef.current = false;
  };

  const { data: agencies } = useGetMyAgencies({});

  const { data: organizations } = useGetTokenOrganizations();

  const { data: clientList } = useGetAllClients({
    // TODO: fix.
    pageSize: 50,
    currentPage: 1,
    search: clientSearch,
    clientName: '',
  });

  const { data: agentList } = useGetAllUsers({
    pageSize: 1000,
    currentPage: 1,
    search: agentSearch,
  });

  const { data: teams } = useGetAllTeams({
    pageSize: 1000,
    currentPage: 1,
    search: teamSearch,
  });

  const { data: workflows } = useGetAllWorkflowsQuery({
    pageSize: 1000,
    currentPage: 1,
    search: workflowSearch,
  });

  // Hook para formatear workflows para custom fields
  const workflowsForCustomFields = React.useMemo(() => {
    let workflowIds: string | undefined;
    
    if (Array.isArray(params.workflowIds)) {
      // Si es array, unir con pipe
      const arrayValues = params.workflowIds as string[];
      workflowIds = arrayValues.length > 0 ? arrayValues.join('|') : undefined;
    } else if (typeof params.workflowIds === 'string' && params.workflowIds.trim() !== '') {
      // Si es string, usar directamente
      workflowIds = params.workflowIds;
    } else {
      workflowIds = undefined;
    }
    
    return workflowIds;
  }, [params.workflowIds]);

  const { data: steps } = useGetStepsByManyWorkflowIds({
    search: stepSearch,
    workflowIds: workflowsForCustomFields ? workflowsForCustomFields.split('|') : [],
  });

  const { data: customFields } = useGetFieldBlockByWorkflowIds({
    workflows: workflowsForCustomFields
  });
  
  const notSelectedOption = {
    id: 'notselected',
    firstName: t('common.unassigned'),
    lastName: '',
  }

  const statusList = [
    {
      id: CaseStatus.canceled,
      name: t(`cases.status.${CaseStatus.canceled}`),
    },
    {
      id: CaseStatus.done,
      name: t(`cases.status.${CaseStatus.done}`),
    },
    {
      id: CaseStatus.inProgress,
      name: t(`cases.status.${CaseStatus.inProgress}`),
    },
    {
      id: CaseStatus.needsAgentAction,
      name: t(`cases.status.${CaseStatus.needsAgentAction}`),
    },
    {
      id: CaseStatus.needsClientAction,
      name: t(`cases.status.${CaseStatus.needsClientAction}`),
    },
    {
      id: CaseStatus.open,
      name: t(`cases.status.${CaseStatus.open}`),
    },
  ];

  const originatedByChannelList = [
    {
      id: OriginatedByChannel.WHATSAPP,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.WHATSAPP}`),
    },
    {
      id: OriginatedByChannel.CALL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.CALL}`),
    },
    {
      id: OriginatedByChannel.EMAIL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EMAIL}`),
    },
    {
      id: OriginatedByChannel.MANUAL,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.MANUAL}`),
    },
    {
      id: OriginatedByChannel.EXTERNAL_INTEGRATION,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.EXTERNAL_INTEGRATION}`),
    },
    {
      id: OriginatedByChannel.UPLOADED,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.UPLOADED}`),
    },
    {
      id: OriginatedByChannel.SUPPORT_FORM,
      name: t(`cases.originatedByChannel.${OriginatedByChannel.SUPPORT_FORM}`),
    },
  ];

  // Opciones para filtros de fecha del pasado
  const pastDateFilterOptions = [
    { value: 'today', label: t('filters.date.today') },
    { value: 'yesterday', label: t('filters.date.yesterday') },
    { value: 'thisWeek', label: t('filters.date.thisWeek') },
    { value: 'thisMonth', label: t('filters.date.thisMonth') },
    { value: 'lastWeek', label: t('filters.date.lastWeek') },
    { value: 'lastMonth', label: t('filters.date.lastMonth') },
    { value: 'customDays', label: t('filters.date.lastNDays') },
    { value: 'specificDate', label: t('filters.date.specificDate') },
    { value: 'dateRange', label: t('filters.date.dateRange') }
  ];

  // Opciones para los filtros de fecha de custom fields (pasado y futuro)
  const allDateFilterOptions = [
    { value: 'today', label: t('filters.date.today') },
    { value: 'yesterday', label: t('filters.date.yesterday') },
    { value: 'thisWeek', label: t('filters.date.thisWeek') },
    { value: 'thisMonth', label: t('filters.date.thisMonth') },
    { value: 'lastWeek', label: t('filters.date.lastWeek') },
    { value: 'lastMonth', label: t('filters.date.lastMonth') },
    { value: 'customDays', label: t('filters.date.lastNDays') },
    { value: 'tomorrow', label: t('filters.date.tomorrow') },
    { value: 'nextWeek', label: t('filters.date.nextWeek') },
    { value: 'nextMonth', label: t('filters.date.nextMonth') },
    { value: 'nextCustomDays', label: t('filters.date.nextNDays') },
    { value: 'specificDate', label: t('filters.date.specificDate') },
    { value: 'dateRange', label: t('filters.date.dateRange') }
  ];

  // Función para manejar cambios en filtros de fecha
  const handleDateFilterChange = (type: 'created' | 'updated', filterType: DateFilterType, additionalData?: any) => {
    // Primero limpiar todos los parámetros existentes para evitar duplicación
    const clearData: Record<string, any> = {
      [`${type}DateFilter`]: '',
      [`${type}CustomDays`]: '',
      [`${type}NextCustomDays`]: '',
      [`${type}SpecificDate`]: '',
      [`${type}After`]: '',
      [`${type}Before`]: '',
      [`${type}DateFrom`]: '',
      [`${type}DateTo`]: ''
    };

    // Obtener los datos adicionales según el tipo de filtro
    let customDays: number | undefined;
    let nextCustomDays: number | undefined;
    let specificDate: string | undefined;
    let dateRange: { from: string; to: string } | undefined;

    if (filterType === 'customDays') {
      customDays = additionalData ? parseInt(additionalData) : (type === 'created' ? parseInt(customDaysCreated) : parseInt(customDaysUpdated));
    } else if (filterType === 'nextCustomDays') {
      nextCustomDays = additionalData ? parseInt(additionalData) : (type === 'created' ? parseInt(customDaysCreated) : parseInt(customDaysUpdated));
    } else if (filterType === 'specificDate') {
      specificDate = additionalData || (type === 'created' ? specificDateCreated : specificDateUpdated);
    } else if (filterType === 'dateRange') {
      dateRange = additionalData || (type === 'created' ? dateRangeCreated : dateRangeUpdated);
    }

    // Calcular las fechas usando las utilidades
    const dateFilter = calculateDateFilter({
      type: filterType,
      customDays,
      nextCustomDays,
      specificDate,
      dateRange,
    });

    // Crear los datos de actualización con los nuevos valores
    const updateData: Record<string, any> = { ...clearData };
    
    if (dateFilter.after) {
      updateData[`${type}After`] = dateFilter.after;
    }
    if (dateFilter.before) {
      updateData[`${type}Before`] = dateFilter.before;
    }

    // También persistir el tipo de filtro para la UI
    updateData[`${type}DateFilter`] = filterType;
    if (filterType === 'customDays' && customDays) {
      updateData[`${type}CustomDays`] = customDays.toString();
    } else if (filterType === 'nextCustomDays' && nextCustomDays) {
      updateData[`${type}NextCustomDays`] = nextCustomDays.toString();
    } else if (filterType === 'specificDate' && specificDate) {
      updateData[`${type}SpecificDate`] = specificDate;
    } else if (filterType === 'dateRange' && dateRange) {
      updateData[`${type}DateFrom`] = dateRange.from;
      updateData[`${type}DateTo`] = dateRange.to;
    }

    setParams(prev => ({ ...prev, ...updateData }));
  };

  // Función para manejar cambios en custom date fields
  const handleCustomDateFieldChange = (fieldKey: string, filterType: DateFilterType, additionalData?: any) => {
    const parts = fieldKey.split('_');
    const fieldBlockId = parts[0];
    const fieldName = parts.length > 1 ? parts.slice(1).join('_') : null;
    
    // Primero limpiar todos los parámetros existentes para evitar duplicación
    const clearData: Record<string, any> = {
      [`${fieldKey}_filter`]: '',
      [`${fieldKey}_customDays`]: '',
      [`${fieldKey}_nextCustomDays`]: '',
      [`${fieldKey}_specificDate`]: '',
      [`${fieldKey}_after`]: '',
      [`${fieldKey}_before`]: ''
    };
    
    // Obtener el estado actual del field o crear uno nuevo
    const currentState = customDateFieldStates[fieldKey] || {
      type: '',
      customDays: '',
      nextCustomDays: '',
      specificDate: '',
      dateRange: { from: '', to: '' }
    };

    // Obtener los datos adicionales según el tipo de filtro
    let customDays: number | undefined;
    let nextCustomDays: number | undefined;
    let specificDate: string | undefined;
    let dateRange: { from: string; to: string } | undefined;

    if (filterType === 'customDays') {
      customDays = additionalData ? parseInt(additionalData) : parseInt(currentState.customDays);
    } else if (filterType === 'nextCustomDays') {
      nextCustomDays = additionalData ? parseInt(additionalData) : parseInt(currentState.nextCustomDays);
    } else if (filterType === 'specificDate') {
      specificDate = additionalData || currentState.specificDate;
    } else if (filterType === 'dateRange') {
      dateRange = additionalData || currentState.dateRange;
    }

    // Calcular las fechas usando las utilidades
    const dateFilter = calculateDateFilter({
      type: filterType,
      customDays,
      nextCustomDays,
      specificDate,
      dateRange,
    });

    // Crear los datos de actualización con los nuevos valores
    const updateData: Record<string, any> = { ...clearData };
    
    if (dateFilter.after) {
      updateData[`${fieldKey}_after`] = dateFilter.after;
    }
    if (dateFilter.before) {
      updateData[`${fieldKey}_before`] = dateFilter.before;
    }

    // También persistir el tipo de filtro para la UI
    updateData[`${fieldKey}_filter`] = filterType;
    if (filterType === 'customDays' && customDays) {
      updateData[`${fieldKey}_customDays`] = customDays.toString();
    } else if (filterType === 'nextCustomDays' && nextCustomDays) {
      updateData[`${fieldKey}_nextCustomDays`] = nextCustomDays.toString();
    } else if (filterType === 'specificDate' && specificDate) {
      updateData[`${fieldKey}_specificDate`] = specificDate;
    }

    setParams(prev => ({ ...prev, ...updateData }));
  };

  // Función para manejar cambios en rangos de fechas sin debounce (para otros tipos)
  const handleDateRangeChange = (type: 'created' | 'updated', field: 'from' | 'to', value: string) => {
    if (type === 'created') {
      const newRange = { ...dateRangeCreated, [field]: value };
      setDateRangeCreated(newRange);
      setDebouncedDateRangeCreated(newRange);
    } else {
      const newRange = { ...dateRangeUpdated, [field]: value };
      setDateRangeUpdated(newRange);
      setDebouncedDateRangeUpdated(newRange);
    }
  };

  // Función para manejar cambios en custom date field ranges
  const handleCustomDateFieldRangeChange = (fieldKey: string, field: 'from' | 'to', value: string) => {
    const currentState = customDateFieldStates[fieldKey];
    
    if (currentState) {
      const newRange = { ...currentState.dateRange, [field]: value };
      updateCustomDateFieldState(fieldKey, { dateRange: newRange });
      
      // Actualizar el estado debounced
      setDebouncedCustomDateFieldStates((prev: Record<string, { from: string; to: string }>) => ({
        ...prev,
        [fieldKey]: newRange
      }));
    }
  };

  // Función para manejar cambios en fechas específicas
  const handleSpecificDateChange = (type: 'created' | 'updated', value: string) => {
    if (type === 'created') {
      setSpecificDateCreated(value);
      setDebouncedSpecificDateCreated(value);
    } else {
      setSpecificDateUpdated(value);
      setDebouncedSpecificDateUpdated(value);
    }
  };

  // Función para manejar cambios en custom date field fechas específicas
  const handleCustomDateFieldSpecificDateChange = (fieldKey: string, value: string) => {
    updateCustomDateFieldState(fieldKey, { specificDate: value });
    
    // Actualizar el estado debounced
    setDebouncedCustomDateFieldSpecificDates((prev: Record<string, string>) => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Función para obtener el estado de un custom date field
  const getCustomDateFieldState = (fieldBlockId: string, fieldName: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    return customDateFieldStates[fieldKey] || {
      type: '',
      customDays: '',
      nextCustomDays: '',
      specificDate: '',
      dateRange: { from: '', to: '' }
    };
  };

  // Función para actualizar el estado de un custom date field
  const updateCustomDateFieldState = (fieldKey: string, updates: Partial<{
    type: string;
    customDays: string;
    nextCustomDays: string;
    specificDate: string;
    dateRange: { from: string; to: string };
  }>) => {
    setCustomDateFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey] || { type: '', customDays: '', nextCustomDays: '', specificDate: '', dateRange: { from: '', to: '' } },
        ...updates
      }
    }));
  };

  // Función para limpiar un custom date field
  const clearCustomDateField = (fieldBlockId: string, fieldName: string) => {
    const fieldKey = `${fieldBlockId}_${fieldName}`;
    const updateData: Record<string, any> = {
      [`${fieldKey}_filter`]: '',
      [`${fieldKey}_customDays`]: '',
      [`${fieldKey}_nextCustomDays`]: '',
      [`${fieldKey}_specificDate`]: '',
      [`${fieldKey}_after`]: '',
      [`${fieldKey}_before`]: ''
    };
    
    setParams(prev => ({ ...prev, ...updateData }));
    
    // Reset estado local
    setCustomDateFieldStates(prev => ({
      ...prev,
      [fieldKey]: {
        type: '',
        customDays: '',
        nextCustomDays: '',
        specificDate: '',
        dateRange: { from: '', to: '' }
      }
    }));
  };

  const handleEditCustomField = (fieldBockId: string, fieldName: string, values: string) => {
    const composedFieldName = `${fieldBockId}_${fieldName}`;
    setParams(prev => ({ ...prev, [composedFieldName]: values }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      await onSave(
        filter.id,
        {
          name,
          isPublic,
          params: params,
        });
    } catch (error) {
      console.error('Error saving filter:', error);
    } finally {
      setIsSaving(false);
    }
  }
  const subjectRef = useRef<HTMLInputElement>()

  return <Box flexDirection="column" display="flex" width="400px">
    <Box
      display="flex"
      pl={2}
      pr={2}
      pb={1}
      justifyContent="space-between"
      alignItems="center"
      alignSelf="stretch"
      borderBottom="1px solid var(--divider, rgba(0, 0, 0, 0.12))"
    >
      <Typography variant="subtitle2" gutterBottom={false}>
        {title}
      </Typography>
      {onClose && <IconButton onClick={onClose}>
        <Close />
      </IconButton>}

    </Box>

    <Box pr={2} pl={2} mt={3} display="flex" flexDirection="column" alignItems="flex-start" gap={1} alignSelf="stretch">
      <Typography variant="body2">{t('filters.nameLabel')}</Typography>
      <TextField variant="outlined" size="small" value={name} placeholder={t('filters.namePlaceholder')} onChange={e => setName(e.target.value)} fullWidth />
      <Box display="flex" gap={1} alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex" gap={1} alignItems="center">
          <Checkbox size="small" color="primary" checked={isPublic} onClick={toggleIsPublic} />
          <Typography variant="body2" >{t('filters.isPublic')}</Typography>
        </Box>
        <Public htmlColor="rgba(0,0,0,.38)" />
      </Box>
      <Divider sx={{ width: '100%' }} />
    </Box>

    <Box display="flex" flexDirection="column" mt={3} gap={3}>
      <FilterItem
        label={t('cases.list.filterBySubject')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            subject: '',
          }))
        }
      >
        <TextField
          inputRef={subjectRef}
          value={params.subject}
          onChange={(e) => {
            setParams(prev => ({ ...prev, subject: e.currentTarget.value }));
          }}
          size="small"
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByOriginatedByChannel')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            originatedByChannel: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={originatedByChannelList || []}
          getOptionLabel={(option) => option.name}
          value={
            originatedByChannelList?.filter((option) => {
              const toRet = Array.isArray(params?.originatedByChannel) ? params.originatedByChannel : [];
              return toRet.includes(String(option.id));
            }) || []
          }
          onChange={(_event, value: { id: OriginatedByChannel; name: string }[]) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              originatedByChannel: valuesForUrl,
            }))
          }}
          renderInput={(fieldParams) => (
            <TextField {...fieldParams} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByStatus')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            status: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={statusList || []}
          getOptionLabel={(option) => option.name}
          value={
            statusList?.filter((option) => {
              const toRet = Array.isArray(params?.status) ? params.status : [];
              return toRet.includes(String(option.id));
            })
          }

          onChange={(_even: unknown, value: { id: CaseStatus; name: string }[]) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              status: valuesForUrl,
            }))
          }}
          renderInput={(fieldParams) => (
            <TextField {...fieldParams} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          inputValue={teamSearch}
          onInputChange={(_event, value) => {
            setTeamSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByTeam')}
        onClear={() =>
          setParams(prev => ({
            ...prev,
            teamIds: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={(teams?.items || []).slice().sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          )}
          getOptionLabel={(option) => option.name}
          value={
            teams?.items.filter((option) => {
              const toRet = Array.isArray(params?.teamIds) ? params.teamIds : [];
              return toRet.includes(String(option.id));
            })
          }
          onChange={(_event, value: TeamResponseDto[]) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              teamIds: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          inputValue={teamSearch}
          onInputChange={(_event, value) => {
            setTeamSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByWorkflow')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            workflowIds: '',
            stepIds: '',
          }))

        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={(workflows?.items || []).slice().sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
          )}
          getOptionLabel={(option) => option.name}
          value={
            workflows?.items.filter((option) => {
              const toRet = Array.isArray(params?.workflowIds) ? params.workflowIds : [];
              return toRet.includes(String(option.id));
            })
          }
          onChange={(_event, value: WorkflowResponseDto[]) => {
            const valuesForUrl = value.map((val) => val.id);
            const myParams: Record<string, string> = { workflowIds: valuesForUrl.join('|') };

            if (!value.length) {
              myParams.stepIds = '';
            }

            setParams(prev => ({
              ...prev,
              ...myParams,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          inputValue={workflowSearch}
          onInputChange={(_event, value) => {
            setWorkflowSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByStep')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            stepIds: '',
          }))
        }>
        <Autocomplete
          fullWidth
          disabled={!params.workflowIds}
          limitTags={1}
          size="small"
          multiple
          options={steps || []}
          getOptionLabel={(option) => option.name}
          value={
            steps?.filter((option) => {
              const toRet = Array.isArray(params?.stepIds) ? params.stepIds : [];
              return toRet.includes(String(option.id));
            })
          }
          onChange={(_event, value: StepOptionsResponseDto[]) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              stepIds: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          inputValue={stepSearch}
          onInputChange={(_event, value) => {
            setStepSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByAgent')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            agentIds: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={(agentList ? [{ ...notSelectedOption }, ...agentList?.items] : []).slice().sort((a, b) =>
            (a.firstName || '').localeCompare(b.firstName || '')
          )}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          value={
            agentList?.items.filter((option) => {
              const toRet = Array.isArray(params?.agentIds) ? params.agentIds : [];
              return toRet.includes(String(option.id));
            })
          }
          onChange={(_event, value) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              agentIds: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.firstName} {option.lastName}
              </li>
            );
          }}
          inputValue={agentSearch}
          onInputChange={(_event, value) => {
            setAgentSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByClient')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            clientIds: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={(clientList ? [{ ...notSelectedOption }, ...clientList?.items] : []).slice().sort((a, b) =>
            (a.firstName || '').localeCompare(b.firstName || '')
          )}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          value={
            clientList ? [{ ...notSelectedOption }, ...clientList?.items].filter((option) => {
              const toRet = Array.isArray(params?.clientIds) ? params.clientIds : [];
              return toRet.includes(String(option.id));
            }) : []
          }
          onChange={(_event, value) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              clientIds: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.firstName} {option.lastName}
              </li>
            );
          }}
          inputValue={clientSearch}
          onInputChange={(_event, value) => {
            setClientSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByOrganization')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            organizations: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={organizations || []}
          getOptionLabel={(option) => option.name}
          value={
            organizations?.filter((option) => {
              const toRet = Array.isArray(params?.organizations) ? params.organizations : [];
              return toRet.includes(option.id);
            })
          }
          onChange={(_event, value) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              organizations: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          inputValue={teamSearch}
          onInputChange={(_event, value) => {
            setTeamSearch(value);
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByAgency')}
        onClear={
          () => setParams(prev => ({
            ...prev,
            agencies: '',
          }))
        }>
        <Autocomplete
          fullWidth
          limitTags={1}
          size="small"
          multiple
          options={agencies || []}
          getOptionLabel={(option) => option.name}
          value={
            agencies?.filter((option) => {
              const toRet = Array.isArray(params?.agencies) ? params.agencies : [];
              return toRet.includes(option.id);
            })
          }
          onChange={(_event, value) => {
            const valuesForUrl = value.map((val) => val.id);
            setParams(prev => ({
              ...prev,
              agencies: valuesForUrl,
            }))
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" fullWidth />
          )}
          renderOption={(props, option, { selected }) => {
            const { ...optionProps } = props;
            return (
              <li {...optionProps} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByCreated')}
        onClear={() => {
          setParams(prev => ({
            ...prev,
            createdDateFilter: '',
            createdCustomDays: '',
            createdSpecificDate: '',
            createdDateFrom: '',
            createdDateTo: '',
            createdAfter: '',
            createdBefore: '',
            createdBeforeDate: '',
            createdAfterDate: ''
          }));
          setCreatedDateType('');
          setCustomDaysCreated('');
          setSpecificDateCreated('');
          setDateRangeCreated({ from: '', to: '' });
        }}
      >
        <Box>
          <Select
            fullWidth
            size="small"
            value={createdDateType}
            onChange={(e) => {
              const value = e.target.value;
              setCreatedDateType(value);
              if (value) {
                handleDateFilterChange('created', value as DateFilterType);
              }
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {t('filters.date.selectDateType')}
            </MenuItem>
            {pastDateFilterOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          
          {createdDateType === 'customDays' && (
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder={t('filters.date.numberOfDays')}
              value={customDaysCreated}
              onChange={(e) => {
                setCustomDaysCreated(e.target.value);
                handleDateFilterChange('created', 'customDays', e.target.value);
              }}
              sx={{ mt: 1 }}
            />
          )}
          
          {createdDateType === 'specificDate' && (
            <TextField
              fullWidth
              size="small"
              type="date"
              value={specificDateCreated}
              onChange={(e) => handleSpecificDateChange('created', e.target.value)}
              sx={{ mt: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          
          {createdDateType === 'dateRange' && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('filters.date.from')}
                value={dateRangeCreated.from}
                onChange={(e) => handleDateRangeChange('created', 'from', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('filters.date.to')}
                value={dateRangeCreated.to}
                onChange={(e) => handleDateRangeChange('created', 'to', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByUpdated')}
        onClear={() => {
          setParams(prev => ({
            ...prev,
            updatedDateFilter: '',
            updatedCustomDays: '',
            updatedSpecificDate: '',
            updatedDateFrom: '',
            updatedDateTo: '',
            updatedAfter: '',
            updatedBefore: ''
          }));
          setUpdatedDateType('');
          setCustomDaysUpdated('');
          setSpecificDateUpdated('');
          setDateRangeUpdated({ from: '', to: '' });
        }}
      >
        <Box>
          <Select
            fullWidth
            size="small"
            value={updatedDateType}
            onChange={(e) => {
              const value = e.target.value;
              setUpdatedDateType(value);
              if (value) {
                handleDateFilterChange('updated', value as DateFilterType);
              }
            }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {t('filters.date.selectDateType')}
            </MenuItem>
            {pastDateFilterOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          
          {updatedDateType === 'customDays' && (
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder={t('filters.date.numberOfDays')}
              value={customDaysUpdated}
              onChange={(e) => {
                setCustomDaysUpdated(e.target.value);
                handleDateFilterChange('updated', 'customDays', e.target.value);
              }}
              sx={{ mt: 1 }}
            />
          )}
          
          {updatedDateType === 'specificDate' && (
            <TextField
              fullWidth
              size="small"
              type="date"
              value={specificDateUpdated}
              onChange={(e) => handleSpecificDateChange('updated', e.target.value)}
              sx={{ mt: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          
          {updatedDateType === 'dateRange' && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('filters.date.from')}
                value={dateRangeUpdated.from}
                onChange={(e) => handleDateRangeChange('updated', 'from', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('filters.date.to')}
                value={dateRangeUpdated.to}
                onChange={(e) => handleDateRangeChange('updated', 'to', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>
      </FilterItem>

      {/* Custom Fields Rendering */}
      {customFields?.map(fieldBlock => (
        <Box key={fieldBlock.id} display="flex" flexDirection="column" pr={2} pl={2} gap={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom={false}>{fieldBlock.name}</Typography>
          </Box>
          {fieldBlock.fields?.map(field => {
            const customDateState = getCustomDateFieldState(fieldBlock.id, field.name);
            
            return (
              <Box key={`${fieldBlock.id}_${field.name}`}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
                  <Typography variant="body2" gutterBottom={false}>{t('filters.filterBy')} {field.label}</Typography>
                  <Link 
                    color="error" 
                    underline="hover" 
                    onClick={() => {
                      if (field.fieldType === 'date') {
                        clearCustomDateField(fieldBlock.id, field.name);
                      } else {
                        handleEditCustomField(fieldBlock.id, field.name, '');
                      }
                    }}
                  >
                    {t('filters.clear')}
                  </Link>
                </Box>
                
                {/* Filtro de fecha avanzado para custom fields */}
                {(field.fieldType === 'date') && (
                  <Box sx={{ mt: 1 }}>
                    <Select
                      fullWidth
                      size="small"
                      value={customDateState.type}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateCustomDateFieldState(`${fieldBlock.id}_${field.name}`, { type: value });
                        if (value) {
                          handleCustomDateFieldChange(`${fieldBlock.id}_${field.name}`, value as DateFilterType);
                        }
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        {t('filters.date.selectDateType')}
                      </MenuItem>
                      {allDateFilterOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    
                    {customDateState.type === 'customDays' && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder={t('filters.date.numberOfDays')}
                        value={customDateState.customDays}
                        onChange={(e) => {
                          updateCustomDateFieldState(`${fieldBlock.id}_${field.name}`, { customDays: e.target.value });
                          handleCustomDateFieldChange(`${fieldBlock.id}_${field.name}`, 'customDays', e.target.value);
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                    
                    {customDateState.type === 'nextCustomDays' && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder={t('filters.date.numberOfDays')}
                        value={customDateState.nextCustomDays}
                        onChange={(e) => {
                          updateCustomDateFieldState(`${fieldBlock.id}_${field.name}`, { nextCustomDays: e.target.value });
                          setDebouncedCustomDateFieldNextCustomDays((prev: Record<string, string>) => ({
                            ...prev,
                            [`${fieldBlock.id}_${field.name}`]: e.target.value
                          }));
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                    
                    {customDateState.type === 'specificDate' && (
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={customDateState.specificDate}
                        onChange={(e) => handleCustomDateFieldSpecificDateChange(`${fieldBlock.id}_${field.name}`, e.target.value)}
                        sx={{ mt: 1 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    
                    {customDateState.type === 'dateRange' && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label={t('filters.date.from')}
                          value={customDateState.dateRange.from}
                          onChange={(e) => handleCustomDateFieldRangeChange(`${fieldBlock.id}_${field.name}`, 'from', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label={t('filters.date.to')}
                          value={customDateState.dateRange.to}
                          onChange={(e) => handleCustomDateFieldRangeChange(`${fieldBlock.id}_${field.name}`, 'to', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
                
                {/* Otros tipos de campos (no date) */}
                {(field.fieldType === 'string' || field.fieldType === 'longtext') && <TextField
                  value={(params[`${fieldBlock.id}_${field.name}`] as string) || ''}
                  size="small"
                  fullWidth
                  onChange={(e) => handleEditCustomField(fieldBlock.id, field.name, e.target.value)}
                />}
                
                {(field.fieldType === 'select' || field.fieldType === 'multiselect') &&
                  <FormControl fullWidth margin="normal">
                    <Select
                      displayEmpty
                      size="small"
                      value={(params[`${fieldBlock.id}_${field.name}`] as string) || ''}
                      onChange={(e) => handleEditCustomField(fieldBlock.id, field.name, e.target.value)}
                      required={field.required}
                    >
                      <MenuItem key={field.label} value={undefined} disabled>
                        {field.label}
                      </MenuItem>
                      {field.options?.map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                }
              </Box>
            );
          })}
        </Box>
      ))}

      <FilterItem
        label={t('cases.list.filterByCreatedAfter')}
        onClear={() =>
          setParams(prev => ({
            ...prev,
            createdAfterDate: '',
          }))
        }
      >
        <TextField
          type="date"
          size="small"
          fullWidth
          value={params.createdAfterDate || ''}
          onChange={(e) =>
            setParams(prev => ({
              ...prev,
              createdAfterDate: e.target.value,
            }))
          }
          sx={{
            // Toda esta magia negra hay q tirarla para q se vea el icono del calendario
            '& input::-webkit-calendar-picker-indicator': {
              opacity: 1,
              display: 'block',
              color: 'red',
              WebkitAppearance: 'auto',
              filter: 'invert(77%) sepia(0%) saturate(0%) hue-rotate(177deg) brightness(92%) contrast(85%)',
            },
          }}
        />
      </FilterItem>

      <FilterItem
        label={t('cases.list.filterByCreatedBefore')}
        onClear={() =>
          setParams(prev => ({
            ...prev,
            createdBeforeDate: '',
          }))
        }
      >
        <TextField
          type="date"
          size="small"
          fullWidth
          value={params.createdBeforeDate || ''}
          onChange={(e) =>
            setParams(prev => ({
              ...prev,
              createdBeforeDate: e.target.value,
            }))
          }
          sx={{
            // Toda esta magia negra hay q tirarla para q se vea el icono del calendario
            '& input::-webkit-calendar-picker-indicator': {
              opacity: 1,
              display: 'block',
              color: 'red',
              WebkitAppearance: 'auto',
              filter: 'invert(77%) sepia(0%) saturate(0%) hue-rotate(177deg) brightness(92%) contrast(85%)',
            },
          }}
        />
      </FilterItem>

    </Box>
    <Box display="flex" p={2} mt={3} justifyContent="space-between" alignItems="flex-start" alignSelf="stretch" borderTop="1px solid var(--divider, rgba(0, 0, 0, 0.12))">
      <Button onClick={handleClearFilters} size="medium" color="error" variant="text">{t('filters.clearAll')}</Button>
      <LoadingButton loading={isSaving} size="medium" color="primary" variant="contained" onClick={handleSave}>{t('common.save')}</LoadingButton>
    </Box>
  </Box>
}